/** @jsx jsx */
import { jsx } from "@emotion/react";
import { Fragment, RefObject, useState } from "react";
import { useInfiniteScroll } from "react-infinite-scroll-hook";
import { useIntl } from "react-intl";
import useDelayedEffect from "src/hooks/utils/useDelayedEffect";
import {
  asyncNoop,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  noop,
} from "../../../../globalConsts";

import {
  RowKey,
  TableColumns,
  TableRow,
} from "../../../../requests/table/types";
import TableDataRowMobile from "./TableDataRowMobile";

import {
  IndicesOfSubmittingInsertRows,
  InsertRows,
  OnInsertCellBlur,
  RemoveInsertRow,
  SubmitInsertRow,
  UpdateTableInsertCell,
} from "../../../../hooks/table/useInsertRows";
import { EditRowWithColumns } from "../../../../requests/tableRowActions/types";
import { ColumnFormatDict } from "../../../../requests/templates/types";
import { withErrorBoundaryHOC } from "../../../atoms/FlowioErrorBoundary";
import Loader, { withLoaderHOC } from "../../../atoms/Loader";
import { mobileRowPositionDataPLACEHOLDER } from "../../ColumnTemplatePositionEditor/placeholderData";
import { MobileRowLayout } from "../../ColumnTemplatePositionEditor/types";
import { noRowsMessageCss } from "../TableDataDesktop/styles";
import { InlineRowEditPropset } from "../TableDataDesktop/TableDataDesktop";
import { ITableRowControls } from "../types";
import {
  MobileRowsContainer,
  MobileRowsFlexWrap,
  MobileRowsPaddingDiv,
  MobileRowsWrapper,
  SelectedRowsCounter,
} from "./styles";

const TableDataMobile = ({
  rows,
  columns,
  totalRowCount,
  loadMoreDataMobile = noop,
  isFromRelationTable = false,
  columnFormatMobileDict,
  allowRowSelection,
  toggleSelectedStatusForRow = noop,
  keysOfSelectedRows = new Set(),
  onMobileRowClick = noop,
  TableRowMobileControls,
  isLoadingMoreMobile,
  inlineRowEditProps,

  positionData,

  insertRows = EMPTY_ARRAY,
  onInsertCellEdit = noop,
  onInsertCellBlur,
  submitInsertRow = asyncNoop,
  removeInsertRow = noop,
  indicesOfSubmittingInsertRows = EMPTY_ARRAY,
}: TableDataMobileProps) => {
  const {
    editRows = EMPTY_OBJECT,
    onCellEdit = noop,
    onCellBlur,
    editColumns = EMPTY_OBJECT,
    submitEditRow = noop,
    resetEditRow = noop,
    hashesOfRowsBeingEdited,
    toggleRowEditMode = noop,
    hashesOfSubmittingEditRows = EMPTY_ARRAY,
  } = inlineRowEditProps ?? {};

  const atLeastOneRowSelected = !!keysOfSelectedRows.size;

  const infiniteRef: RefObject<HTMLDivElement> = useInfiniteScroll({
    loading: isLoadingMoreMobile,
    hasNextPage: rows.length < totalRowCount,
    onLoadMore: loadMoreDataMobile,
    scrollContainer: "window",
    checkInterval: 50,
    threshold: 300,
  });

  const [rowState, setRow] = useState<string>("");
  const [newRowState, setNewRow] = useState<number>(-1);
  const [newOrEdit, setNewOrEdit] = useState<"NEW" | "EDIT" | "">("");

  useDelayedEffect(() => {
    setNewOrEdit("EDIT");
  }, [rowState]);

  useDelayedEffect(() => {
    setNewOrEdit("NEW");
  }, [newRowState]);

  const handleSubmit = (): void => {
    if (newOrEdit === "EDIT") {
      submitEditRow(rowState);
      setNewOrEdit("");
    }
    if (newOrEdit === "NEW") {
      submitInsertRow(newRowState);
      setNewOrEdit("");
      setNewRow(-1);
    }
  };

  const { formatMessage } = useIntl();

  if (rows.length === 1 && isFromRelationTable) {
    onMobileRowClick({ row: rows[0], columns });
  }

  if (!totalRowCount && !insertRows.length) {
    return (
      <Fragment>
        <div css={noRowsMessageCss}>
          {formatMessage({ id: "table.no_rows" })}
        </div>
        {isLoadingMoreMobile && <Loader size={30} />}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <MobileRowsContainer ref={infiniteRef}>
        <MobileRowsWrapper>
          <MobileRowsFlexWrap>
            <MobileRowsPaddingDiv>
              {insertRows.map((row, index) => (
                <TableDataRowMobile
                  row={row}
                  columns={editColumns}
                  columnFormatMobileDict={columnFormatMobileDict}
                  editMode
                  onEditRowSubmit={() => setNewRow(index)}
                  onEditRowReset={() => removeInsertRow(index)}
                  updateEditRow={onInsertCellEdit.bind(null, index)}
                  onCellBlur={() => onInsertCellBlur?.(index)}
                  isLoading={indicesOfSubmittingInsertRows.includes(index)}
                  positionData={
                    positionData || mobileRowPositionDataPLACEHOLDER
                  }
                  onRowClick={noop}
                  rowSelectMode={false}
                  allowRowSelection={false}
                  onRowSelect={noop}
                  isSelected={false}
                  handleSubmit={() => submitInsertRow(index)}
                />
              ))}

              {rows.map((row) => {
                const { hash: rowHash, keyValue } = row;

                const editMode = hashesOfRowsBeingEdited?.includes(rowHash);

                const rowToUse = editMode ? editRows[rowHash] : row;
                const columnsToUse = editMode ? editColumns : columns;

                const isEditRowLoading = editMode && !editRows[rowHash];
                const isEditRowSubmitting =
                  hashesOfSubmittingEditRows?.includes(rowHash);

                return (
                  <TableDataRowMobile
                    key={rowHash}
                    row={rowToUse}
                    columns={columnsToUse}
                    onEditRowSubmit={() => setRow(rowHash)}
                    onEditRowReset={() => resetEditRow(rowHash)}
                    editMode={editMode}
                    updateEditRow={onCellEdit.bind(null, rowHash)}
                    onCellBlur={() => onCellBlur?.(rowHash)}
                    isLoading={isEditRowLoading || isEditRowSubmitting}
                    toggleEditMode={() => toggleRowEditMode(rowHash)}
                    columnFormatMobileDict={columnFormatMobileDict}
                    onRowClick={() => onMobileRowClick({ row, columns })}
                    rowSelectMode={atLeastOneRowSelected}
                    allowRowSelection={allowRowSelection}
                    onRowSelect={() => toggleSelectedStatusForRow(keyValue)}
                    isSelected={keysOfSelectedRows.has(row.keyValue)}
                    TableRowMobileControls={TableRowMobileControls}
                    positionData={
                      positionData || mobileRowPositionDataPLACEHOLDER
                    }
                    handleSubmit={() => submitEditRow(rowHash)}
                  />
                );
              })}
            </MobileRowsPaddingDiv>
          </MobileRowsFlexWrap>
        </MobileRowsWrapper>
      </MobileRowsContainer>
      {isLoadingMoreMobile && <Loader size={30} />}
      <SelectedRowsCounter visible={atLeastOneRowSelected}>
        {keysOfSelectedRows.size}
      </SelectedRowsCounter>
    </Fragment>
  );
};

export type TableDataMobileProps = {
  isFromRelationTable?: boolean;
  rows: TableRow[];
  columns: TableColumns;
  totalRowCount: number;
  loadMoreDataMobile?: () => void;
  isLoadingMoreMobile: boolean;
  columnFormatMobileDict: ColumnFormatDict;
  inlineRowEditProps?: InlineRowEditPropset;

  allowRowSelection: boolean;
  toggleSelectedStatusForRow?: (rowKey: RowKey) => void;
  keysOfSelectedRows?: Set<RowKey>;

  onMobileRowClick?: (row: EditRowWithColumns) => void;
  TableRowMobileControls?: ITableRowControls | null;
  positionData?: MobileRowLayout | null;

  insertRows?: InsertRows;
  onInsertCellEdit?: UpdateTableInsertCell;
  onInsertCellBlur?: OnInsertCellBlur;
  submitInsertRow?: SubmitInsertRow;
  removeInsertRow?: RemoveInsertRow;
  indicesOfSubmittingInsertRows?: IndicesOfSubmittingInsertRows;
};

export default withErrorBoundaryHOC(withLoaderHOC(TableDataMobile));
